/**
 * Unified Authentication Context
 * Primary authentication using Supabase Auth with Mock fallback for development
 */

import React, { ReactNode } from 'react';
import { MockAuthProvider, useMockAuth } from './MockAuthContext';
import { SupabaseAuthProvider, useAuth as useSupabaseAuth } from './SupabaseAuthContext';

// Configuration checks
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const SUPABASE_AUTH_ENABLED = import.meta.env.VITE_SUPABASE_AUTH_ENABLED === 'true';
const ENABLE_MOCK_AUTH = import.meta.env.VITE_ENABLE_MOCK_AUTH === 'true';

// Determine authentication system to use
// Priority: Supabase (if configured) > Mock (if enabled) > Error
const HAS_SUPABASE_CONFIG = SUPABASE_URL && SUPABASE_ANON_KEY;
const USE_SUPABASE = HAS_SUPABASE_CONFIG && (SUPABASE_AUTH_ENABLED || !ENABLE_MOCK_AUTH);
const USE_MOCK = !USE_SUPABASE && ENABLE_MOCK_AUTH;

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Unified Auth Provider
 * Automatically selects the appropriate auth provider based on configuration
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  if (USE_SUPABASE) {
    return <SupabaseAuthProvider>{children}</SupabaseAuthProvider>;
  } else if (USE_MOCK) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  } else {
    // Enhanced error handling with configuration details
    // Provide detailed error message for different scenarios
    let errorMessage = 'Authentication system not properly configured. ';
    
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      errorMessage += 'Missing Supabase credentials (VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY). ';
    }
    
    if (!ENABLE_MOCK_AUTH && (!SUPABASE_URL || !SUPABASE_ANON_KEY)) {
      errorMessage += 'Either configure Supabase credentials or enable mock authentication with VITE_ENABLE_MOCK_AUTH=true for development.';
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Unified useAuth hook
 * Returns the appropriate auth context based on configuration
 */
export const useAuth = () => {
  if (USE_SUPABASE) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useSupabaseAuth();
  } else if (USE_MOCK) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMockAuth();
  } else {
    // Enhanced error with same detailed message as AuthProvider
    let errorMessage = 'Authentication system not properly configured. ';
    
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      errorMessage += 'Missing Supabase credentials (VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY). ';
    }
    
    if (!ENABLE_MOCK_AUTH && (!SUPABASE_URL || !SUPABASE_ANON_KEY)) {
      errorMessage += 'Either configure Supabase credentials or enable mock authentication with VITE_ENABLE_MOCK_AUTH=true for development.';
    }
    
    throw new Error(errorMessage);
  }
};

// Export the User type from Supabase context (primary)
export type { User } from './SupabaseAuthContext';

// Export helper functions to check which auth system is active
export const isSupabaseAuth = () => USE_SUPABASE;
export const isMockAuth = () => USE_MOCK;
export const getAuthSystem = () => {
  if (USE_SUPABASE) return 'supabase';
  if (USE_MOCK) return 'mock';
  return 'none';
};

export default AuthProvider;
